<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ButtonType, CustomButton } from '@/components/common/';
import type { CaseModel, FormField } from '@/models';
import { useStudyStore } from '@/stores/study';
import { useCaseNoteStore } from '@/stores/caseNote';
import CaseNoteDialog from '@/components/data/pipeline/CaseNoteDialog.vue';
import CaseNotesPanel from '@/components/data/pipeline/CaseNotesPanel.vue';
import { getPumpTimepointOptions } from '@/components/data/pipeline/caseNoteTimepoints';

/**
 * The side rail of the entry dialog. The full-page entry has room to float the incomplete-fields list beside the form
 * and to dock case notes as a pinned overlay; inside a dialog both have to live in a column the dialog owns, one at a
 * time.
 */
const props = defineProps({
  caseModel: {
    type: Object as PropType<CaseModel>
  },
  unansweredFields: {
    type: Array as PropType<FormField[]>,
    default: () => []
  }
});

/** `railState` lets the dialog size itself around the rail, so opening the rail widens the dialog instead of squeezing
 * the form. */
const emit = defineEmits(['onFieldClick', 'railState']);

const studyStore = useStudyStore();
const caseNoteStore = useCaseNoteStore();

type RailView = 'incomplete' | 'notes';
const activeView = ref<RailView>();
const showAddNoteDialog = ref(false);

const canShowCaseNotes = computed(() => {
  const flags = studyStore.selectedStudy?.flags as Record<string, unknown> | undefined;
  return flags?.['enableCaseNotes'] === true && props.caseModel !== undefined;
});

/** The case row's count is the reliable source until this rail's own notes view has loaded them — `caseNoteStore.notes`
 * is shared, and a pinned panel on the view behind the dialog may be holding another case's notes. */
const noteCount = computed(() => activeView.value === 'notes' && caseNoteStore.notes.length ?
  caseNoteStore.notes.length :
  props.caseModel?.caseNoteCount ?? 0);

const hasNotes = computed(() => canShowCaseNotes.value);

/** Nothing to show means no rail at all — not an empty strip of tabs. */
const hasContent = computed(() => props.unansweredFields.length > 0 || hasNotes.value);

const timepointOptions = computed(
  () => getPumpTimepointOptions(props.caseModel, studyStore.selectedStudy)
);

const toggleView = (view: RailView) => {
  activeView.value = activeView.value === view ? undefined : view;
};

watch(
  [hasContent, activeView],
  ([contentPresent, view]) => {
    if (!contentPresent && view) {
      activeView.value = undefined;
      return;
    }
    emit('railState', !contentPresent ? 'hidden' : view ? 'open' : 'collapsed');
  },
  { immediate: true }
);

/** Open the incomplete list on its own once the entry reports unanswered fields, and drop it again when they are all
 * filled in, so the rail doesn't sit open and empty. */
watch(
  () => props.unansweredFields.length,
  (count, previousCount) => {
    if (count > 0 && !previousCount && !activeView.value) {
      activeView.value = 'incomplete';
    } else if (count === 0 && activeView.value === 'incomplete') {
      activeView.value = undefined;
    }
  },
  { immediate: true }
);

const onNoteSaved = async () => {
  showAddNoteDialog.value = false;
  if (props.caseModel) {
    await caseNoteStore.getNotes(props.caseModel.identifier);
  }
};
</script>

<template>
  <div class="entry-rail"
    v-if="hasContent"
    :class="{ 'entry-rail-open': activeView !== undefined }">
    <div class="entry-rail-tabs">
      <button type="button"
        class="entry-rail-tab"
        :class="{ active: activeView === 'incomplete' }"
        v-if="unansweredFields.length > 0"
        @click="toggleView('incomplete')">
        <FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" class="entry-rail-tab-icon incomplete" />
        <span class="entry-rail-tab-count">{{ unansweredFields.length }}</span>
      </button>

      <button type="button"
        class="entry-rail-tab"
        :class="{ active: activeView === 'notes' }"
        v-if="hasNotes"
        @click="toggleView('notes')">
        <FontAwesomeIcon :icon="['far', 'note-sticky']" class="entry-rail-tab-icon" />
        <span class="entry-rail-tab-count" v-if="noteCount > 0">
          {{ noteCount > 99 ? '99+' : noteCount }}
        </span>
      </button>
    </div>

    <div class="entry-rail-panel" v-if="activeView === 'incomplete'">
      <div class="entry-rail-header">
        <span class="entry-rail-title">Incomplete Fields</span>
        <CustomButton :type="ButtonType.ICON_ONLY"
          :icon="{ icon: 'xmark', style: 'fas' }"
          tooltip="Close"
          @onClick="activeView = undefined"/>
      </div>
      <ul class="entry-rail-list">
        <li v-for="field in unansweredFields"
          :key="field.identifier"
          class="incomplete-field"
          tabindex="0"
          @click="emit('onFieldClick', field)"
           @keydown.enter.prevent="emit('onFieldClick', field)"
           @keydown.space.prevent="emit('onFieldClick', field)">
          <FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" class="incomplete-icon" />
          <span class="incomplete-label">{{ field.label }}</span>
        </li>
      </ul>
    </div>

    <!-- Kept mounted and toggled with `v-show`: the panel loads its notes when `visible` flips, so it has to be around
         to observe that change. -->
    <div class="entry-rail-panel"
      v-if="caseModel"
      v-show="activeView === 'notes'">
      <CaseNotesPanel inline
        :visible="activeView === 'notes'"
        :case-identifier="caseModel.identifier"
        :case-label="caseModel.label"
        :timepoint-options="timepointOptions"
        :show-pin-button="false"
        @onClose="activeView = undefined"
        @onAddNote="showAddNoteDialog = true"/>
    </div>

    <CaseNoteDialog v-if="caseModel"
      :visible="showAddNoteDialog"
      :case-identifier="caseModel.identifier"
      :timepoint-options="timepointOptions"
      @onClose="showAddNoteDialog = false"
      @onSaved="onNoteSaved"/>
  </div>
</template>

<style scoped lang="scss">
@use "../../../assets/variables.module" as *;

// Widths come from the custom properties the entry dialog declares, so the dialog can grow by exactly the space the
// rail needs rather than the rail eating into the form. See EntryDialog.vue.
.entry-rail {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  width: var(--entry-rail-tabs-width, 52px);
  border-left: 1px solid #ced3d9;
  background-color: #f5f7f7;
}
.entry-rail-open {
  // Never more than 40% of a narrow dialog, where there is no room to grow and the form has to give something up.
  width: calc(
    var(--entry-rail-tabs-width, 52px) + min(var(--entry-rail-panel-width, 360px), 40vw)
  );

  // On a phone there is no width to share — 40% would leave the form unusable. Cover it instead, so the form keeps its
  // layout width (a zero-width virtualized list would have to re-measure) and the user closes the rail to get back.
  @media (max-width: 600px) {
    position: absolute;
    inset: 0;
    width: 100%;
    z-index: 2;
  }
}
.entry-rail-tabs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 auto;
  width: var(--entry-rail-tabs-width, 52px);
  padding: 12px 8px;
  border-right: 1px solid #ced3d9;
}
.entry-rail-tab {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: none;
  cursor: pointer;

  &:hover {
    background-color: #e6eaec;
  }

  &.active {
    background-color: #fff;
    border-color: #ced3d9;
  }
}
.entry-rail-tab-icon {
  font-size: 16px;
  color: #575e66;

  &.incomplete {
    color: #e67e22;
  }
}
.entry-rail-tab-count {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  border-radius: 8px;
  background: var(--p-primary-color, #{$primary-color});
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
}
.entry-rail-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
}
.entry-rail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.entry-rail-title {
  font-weight: 700;
  color: #2a3a4b;
}
.entry-rail-list {
  list-style: none;
  padding: 0;
  margin: 0;

  .incomplete-field {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 4px 0 rgba(60, 80, 120, 0.04);
    color: #2a3a4b;
    cursor: pointer;
    outline: none;

    &:hover,
    &:focus {
      background: #eaf3fa;
    }

    .incomplete-icon {
      color: #e67e22;
    }

    .incomplete-label {
      flex: 1;
      font-weight: 500;
      word-break: break-word;
    }
  }
}
</style>
